import { ChangeDetectionStrategy, Component, computed, input, output } from '@angular/core';
import { Quadrant, Tooth } from '@libs/ui-lib';
import { TranslateModule } from '@ngx-translate/core';
import { TeethComponent } from '../../../teeth';

@Component({
  selector: 'lib-quadrant-cell',
  standalone: true,
  templateUrl: './quadrant-cell.component.html',
  styleUrl: './quadrant-cell.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [TeethComponent, TranslateModule],
})
export class QuadrantCellComponent {
  public readonly teeth = input.required<Tooth[]>();
  public readonly selectedTeethPositions = input.required<number[]>();
  public readonly drawTreatments = input.required<boolean>();
  public readonly quadrant = input.required<Quadrant>();
  public readonly showBackground = input<boolean>(false);
  public readonly paddingClass = input<string>('');
  public readonly isClickable = input<boolean>(false);
  public readonly isSelected = input<boolean>(false);
  public readonly showLabel = input<boolean>(false);

  public readonly clicked = output<number>();
  public readonly quadrantClicked = output<Quadrant>();

  protected readonly quadrantLabel = computed(() => {
    const quadrant = this.quadrant();
    switch (quadrant) {
      case Quadrant.TopLeft: {
        return 'charting_add_treatment_quadrant_top_left';
      }
      case Quadrant.TopRight: {
        return 'charting_add_treatment_quadrant_top_right';
      }
      case Quadrant.BottomLeft: {
        return 'charting_add_treatment_quadrant_bottom_left';
      }
      case Quadrant.BottomRight: {
        return 'charting_add_treatment_quadrant_bottom_right';
      }
      default: {
        return '';
      }
    }
  });

  protected readonly cornerRoundingClass = computed(() => {
    const quadrant = this.quadrant();
    const padding = this.paddingClass();
    const isClickable = this.isClickable();
    const isSelected = this.isSelected();
    const showBg = this.showBackground();

    let baseClasses = 'flex w-full flex-1 justify-center rounded-2xl';

    // Add corner rounding
    switch (quadrant) {
      case Quadrant.TopLeft: {
        baseClasses += ' rounded-tr-4xl';
        break;
      }
      case Quadrant.TopRight: {
        baseClasses += ' rounded-tl-4xl';
        break;
      }
      case Quadrant.BottomLeft: {
        baseClasses += ' rounded-br-4xl';
        break;
      }
      case Quadrant.BottomRight: {
        baseClasses += ' rounded-bl-4xl';
        break;
      }
    }

    // Add background
    if (showBg) {
      baseClasses += ' bg-background-white';
    }

    // Add clickable styles
    if (isClickable) {
      baseClasses += ' cursor-pointer border-2';
      baseClasses += isSelected
        ? ' !border-primary-blue !bg-background-light-blue'
        : ' border-background-white hover:border-background-light-blue hover:bg-background-light-blue';
    }

    // Add padding
    if (padding) {
      baseClasses += ` ${padding}`;
    }

    return baseClasses;
  });

  protected readonly isUpperQuadrant = computed(() => {
    return this.quadrant() === Quadrant.TopLeft || this.quadrant() === Quadrant.TopRight;
  });

  protected onQuadrantClick(): void {
    if (this.isClickable()) {
      this.quadrantClicked.emit(this.quadrant());
    }
  }
}
